import { EntityType } from '@frontend/common';
import { ResolveFunction, useReactiveRow } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { Row } from '@tanstack/react-table';
import React, { JSX } from 'react';

export interface ReactiveRowProps {
    className?: string;
    expandEnable?: boolean;
    selectEnabled?: boolean;
    row: Row<any>;
    onClick?: (element: any) => void;
    /** True while the entity behind this row is deleted but still shown during the grace period. */
    isPendingDelete?: boolean;
    /** Changes when table column visibility/order changes, forcing visible cells to be recalculated. */
    columnLayoutKey?: string;
}

interface Props {
    className?: string;
    expandEnabled?: boolean;
    selectEnabled?: boolean;
    row: Row<any>;
    onClick?: (obj: any) => void;
    RowElement: (props: ReactiveRowProps) => JSX.Element;

    dispatch: ThunkDispatch<any, any, Action>;
    listId: string;
    entityType: EntityType;
    pendingDeleteIds: string[];
    resolveFunction?: ResolveFunction;
    onRowFallback?: () => void;
    columnLayoutKey?: string;
}

/**
 * Renders one row and keeps it in sync with its own entity.
 *
 * Only top level rows subscribe: sub rows live inside their parent's `children` array rather than
 * in the list results, so there is nothing for a patch to write back to.
 */
function ReactiveTRowComponent(props: Props) {
    const rowOriginal = props.row.original as { id?: string; account_id?: string } | undefined;
    const rowId = rowOriginal?.id;

    useReactiveRow({
        listId: props.listId,
        entityType: props.entityType,
        id: props.row.depth === 0 ? rowId : undefined,
        accountId: rowOriginal?.account_id,
        dispatch: props.dispatch,
        resolveFunction: props.resolveFunction,
        onUnresolvableUpdate: props.onRowFallback
    });

    return (
        <>
            <props.RowElement
                className={props.className}
                row={props.row}
                selectEnabled={props.selectEnabled}
                expandEnable={props.expandEnabled}
                onClick={props.onClick}
                isPendingDelete={rowId != null && props.pendingDeleteIds.includes(rowId)}
                columnLayoutKey={props.columnLayoutKey}
            />

            {props.row.getIsExpanded() &&
                props.row.subRows.map((subRow) => (
                    <ReactiveTRow
                        {...props}
                        className={props.className}
                        key={subRow.id}
                        row={subRow}
                    />
                ))}
        </>
    );
}

export const ReactiveTRow = React.memo(ReactiveTRowComponent, (prev, next) => {
    const prevId = (prev.row.original as { id?: string } | undefined)?.id;
    const nextId = (next.row.original as { id?: string } | undefined)?.id;
    const prevPendingDelete = prevId != null && prev.pendingDeleteIds.includes(prevId);
    const nextPendingDelete = nextId != null && next.pendingDeleteIds.includes(nextId);

    return (
        prev.row.original === next.row.original &&
        prev.row.getIsExpanded() === next.row.getIsExpanded() &&
        prev.columnLayoutKey === next.columnLayoutKey &&
        prevPendingDelete === nextPendingDelete &&
        prev.className === next.className &&
        prev.selectEnabled === next.selectEnabled &&
        prev.expandEnabled === next.expandEnabled &&
        prev.pendingDeleteIds === next.pendingDeleteIds
    );
});
